import { gql, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import { getFirstQueryResult } from '../../utils/graphql';
import { useQueryWithMsg, useUrlQuery } from '../../utils/hooks';
import { navigateToPortal } from '../../utils/utils';
import ArrowIcon from '../icons/ArrowIcon';
import OnboardingLayout from './OnboardingLayout';
import PlanContent from './PlanContent';
import ReviewContent from './ReviewContent';
import TeamContent from './TeamContent';

const CreateStripeCheckoutSessionMutation = gql`
  mutation CreateStripeCheckoutSessionMutation(
    $input: CreateStripeCheckoutSessionInput!
  ) {
    createStripeCheckoutSession(input: $input) {
      errors
      result {
        stripeCheckoutSession {
          id
          url
        }
      }
    }
  }
`;

const ExpireStripeCheckoutSessionMutation = gql`
  mutation ExpireStripeCheckoutSessionMutation(
    $input: ExpireStripeCheckoutSessionInput!
  ) {
    expireStripeCheckoutSession(input: $input) {
      errors
    }
  }
`;

const TeamCheckoutQuery = gql`
  query TeamCheckoutQuery($id: ID!) {
    teamStripeState(id: $id) {
      id
      name
      updatedAt
      stripeSubscription {
        id
      }
      stripeCheckoutSession {
        id
        priceId
        quantity
        url
      }
    }
  }
`;

const PricesQuery = gql`
  query BillingPrices {
    billingPrices {
      prices {
        id
        lookupKey
        unitAmount
      }
    }
  }
`;

const stages = [
  {
    name: 'team',
    image: '/images/onboard1.webp',
  },
  { name: 'plan', image: '/images/onboard2.webp' },
  { name: 'review', image: '/images/onboard3.webp' },
];

const BackButton = styled((props) => (
  <Button color="link" {...props}>
    <ArrowIcon direction="left" />
    Back
  </Button>
))`
  svg {
    position: absolute;
    left: -50px;
    width: 22px;
    height: 22px;
  }
  padding: 0;
  margin: 0;
`;

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const queryParams = useUrlQuery();
  const backPath = queryParams.get('backPath');
  const { teamId } = useParams();

  // Get the pre-existing team object if provided
  const { msg, data: existingTeamData } = useQueryWithMsg(TeamCheckoutQuery, {
    variables: { id: teamId },
    skip: !teamId,
  });

  // Preload the price data here so that it's probably ready when
  // by the time we show the prices view.
  const priceResult = useQueryWithMsg(PricesQuery);

  // State
  const [stage, setStage] = useState(0);
  const [team, setTeam] = useState(null);
  const [plan, setPlan] = useState(null);
  const [stripeCheckoutSession, setStripeCheckoutSession] = useState(null);

  const [createStripeCheckoutSession] = useMutation(
    CreateStripeCheckoutSessionMutation,
    {
      onCompleted: ({ createStripeCheckoutSession: { result } }) => {
        setStripeCheckoutSession(result?.stripeCheckoutSession);
      },
    },
  );

  const [expireStripeCheckoutSession] = useMutation(
    ExpireStripeCheckoutSessionMutation,
  );
  const expireCheckoutSession = () => {
    if (!stripeCheckoutSession || !team) return;
    expireStripeCheckoutSession({
      variables: {
        input: {
          id: team.id,
          checkoutSessionId: stripeCheckoutSession.id,
        },
      },
    });
  };

  const { name: stageName, image } = stages[stage];
  const incrementStage = () => setStage(stage + 1);
  const decrementStage = () => {
    if (stage === 0) {
      navigate(backPath || '/app/my-projects');
    } else {
      if (stageName === 'review') {
        setStripeCheckoutSession(null);
        expireCheckoutSession();
      }
      setStage(stage - 1);
    }
  };

  const onEditPlan = () => {
    if (stage === 3) {
      expireCheckoutSession();
    }
    setStage(1); // Edit plan
  };

  useEffect(() => {
    const teamData = getFirstQueryResult(existingTeamData);
    if (teamData?.stripeSubscription?.id) {
      const continueUrl = `${
        new URL(window.location.href).origin
      }/app/team/${teamId}/farms`;
      navigateToPortal({ teamId: teamData?.id, continueUrl });
    }
    if (teamData) {
      setTeam(teamData);

      if (teamData?.stripeCheckoutSession?.url) {
        // Wait until the price data is loaded
        if (!priceResult.data) return;
        const checkoutSession = teamData.stripeCheckoutSession;
        const stripePrice = priceResult.data.billingPrices.prices.find(
          (p) => p.id === checkoutSession.priceId,
        );
        setStage(2); // Review
        setTeam(teamData);
        setPlan({
          stripePrice,
          seats: checkoutSession.quantity,
        });
        setStripeCheckoutSession(checkoutSession);
      } else {
        setStage(1); // Edit plan
      }
    }
  }, [existingTeamData, priceResult.data, teamId]);

  return (
    <OnboardingLayout image={image}>
      {msg ? (
        <h2>{msg}</h2>
      ) : (
        <>
          {stageName !== 'invite' && (
            <BackButton onClick={decrementStage} className="mb-5" />
          )}
          {stageName === 'team' && (
            <TeamContent
              onCompleted={(result) => {
                if (teamId) {
                  setTeam(result);
                  incrementStage();
                } else {
                  // This will navigate to the next step and also
                  // get the new teamId into the url history stack.
                  navigate(
                    `/app/team/${result.id}/onboard${window.location.search}`,
                    {
                      replace: true,
                    },
                  );
                }
              }}
              team={team}
            />
          )}
          {stageName === 'plan' && (
            <PlanContent
              priceResult={priceResult}
              initialValue={plan}
              team={team}
              onCompleted={({ seats, stripePrice }) => {
                setPlan({ seats, stripePrice });
                createStripeCheckoutSession({
                  variables: {
                    input: {
                      id: team.id,
                      priceId: stripePrice.id,
                      quantity: seats,
                    },
                  },
                });
                incrementStage();
              }}
            />
          )}
          {stageName === 'review' && (
            <ReviewContent
              plan={plan}
              onEditPlan={onEditPlan}
              team={team}
              checkoutUrl={stripeCheckoutSession?.url}
            />
          )}
        </>
      )}
    </OnboardingLayout>
  );
};

export default OnboardingFlow;
